/**
 * The booking state machine.
 *
 *   enquiry ──> option ──> confirmed ──> stayed ──> settled ──> closed
 *      │           │            │                       │
 *      └─ declined └─ expired   └─ cancelled            └─ reopen ─> stayed
 *
 * Mistakes must be fixable (0.2b): declined, cancelled and expired all step
 * back to `enquiry` — an enquiry holds no dates, so re-taking an option redoes
 * the occupancy check through the front door. Only `closed` has no exit.
 *
 * Every transition goes through `applyTransition`. Nothing anywhere else in the
 * system may assign a status directly — that is what keeps the audit trail
 * complete (NFR-7) and the legal-document lifecycle honest.
 *
 * Adding a state means adding a row to TRANSITIONS, not scattering another
 * `if (status === ...)` through the handlers.
 */

export const BOOKING_STATUSES = [
  "enquiry",
  "option",
  "confirmed",
  "stayed",
  "settled",
  "closed",
  "declined",
  "expired",
  "cancelled",
] as const;

export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const BOOKING_TRANSITIONS = [
  "hold_option",
  "decline",
  "expire",
  "confirm",
  "cancel",
  "record_stay",
  "settle",
  "close",
  "un_decline",
  "un_cancel",
  "revive",
  "reopen",
  "un_record",
] as const;

export type BookingTransition = (typeof BOOKING_TRANSITIONS)[number];

type TransitionRule = {
  readonly from: readonly BookingStatus[];
  readonly to: BookingStatus;
  /** Dutch label for the audit log and the admin timeline. */
  readonly label: string;
};

const TRANSITIONS: Record<BookingTransition, TransitionRule> = {
  hold_option: { from: ["enquiry"], to: "option", label: "Optie genomen" },
  decline: { from: ["enquiry"], to: "declined", label: "Aanvraag geweigerd" },
  expire: { from: ["option"], to: "expired", label: "Optie vervallen" },
  confirm: { from: ["option"], to: "confirmed", label: "Bevestigd" },
  cancel: { from: ["option", "confirmed"], to: "cancelled", label: "Geannuleerd" },
  record_stay: { from: ["confirmed"], to: "stayed", label: "Verblijf afgerond" },
  settle: { from: ["stayed"], to: "settled", label: "Afgerekend" },
  close: { from: ["settled"], to: "closed", label: "Afgesloten" },
  // The escape hatches (0.2b). All three step back to `enquiry`, never
  // straight to `option`: an enquiry holds no dates, so the occupancy
  // constraint gets its say again when the option is re-taken.
  un_decline: { from: ["declined"], to: "enquiry", label: "Weigering ongedaan gemaakt" },
  un_cancel: { from: ["cancelled"], to: "enquiry", label: "Annulering ongedaan gemaakt" },
  revive: { from: ["expired"], to: "enquiry", label: "Vervallen aanvraag heropend" },
  // Reopen voids the settlement — the caller must do that in the SAME
  // statement as this transition (see the API's reopen writer).
  reopen: { from: ["settled"], to: "stayed", label: "Afrekening heropend" },
  // The one-tap trap the persona review walked into: a stay recorded four
  // weeks early had no way back. Both states occupy and freeze prices, so
  // stepping back is side-effect-free.
  un_record: { from: ["stayed"], to: "confirmed", label: "Verblijfsregistratie ongedaan gemaakt" },
};

/**
 * How long an option holds the dates before it expires. rules/domain.md says
 * "a configurable number of days" — this constant is the configuration until
 * an admin-settings screen exists. Shared by the seed and the transition
 * endpoint so the demo data and live behaviour cannot disagree.
 */
export const OPTION_HOLD_DAYS = 14;

/**
 * End states. Since 0.2b most have an escape hatch — TERMINAL is about what
 * ENTERING the state means (the UI asks before a transition into one), not
 * about being inescapable. Only `closed` truly is.
 */
const TERMINAL: readonly BookingStatus[] = ["closed", "declined", "expired", "cancelled"];

/** Statuses in which the price is frozen. See `.claude/rules/domain.md` for why. */
const PRICES_FROZEN_IN: readonly BookingStatus[] = [
  "confirmed",
  "stayed",
  "settled",
  "closed",
  "cancelled",
  // Dead bookings are frozen too — there is no such thing as editing the price
  // of a declined enquiry.
  "declined",
  "expired",
];

/** An append-only record of one transition. `actorId` is an id, never a name (NFR-6). */
export type AuditEvent = {
  readonly bookingId: string;
  readonly transition: BookingTransition;
  readonly from: BookingStatus;
  readonly to: BookingStatus;
  readonly label: string;
  readonly actorId: string;
  /** ISO-8601 UTC. Passed in — the domain never reads a clock. */
  readonly at: string;
  readonly note?: string;
};

export type TransitionContext = {
  readonly bookingId: string;
  readonly actorId: string;
  readonly at: Date;
  readonly note?: string;
};

/**
 * Failure is machine-readable, not a sentence. The Dutch a user sees is looked
 * up in `packages/web/src/copy/nl.ts` from `reason`, `from` and `transition` —
 * the domain has no business composing copy, and cannot be tested for it.
 */
export type TransitionResult =
  | { readonly ok: true; readonly status: BookingStatus; readonly event: AuditEvent }
  | {
      readonly ok: false;
      readonly reason: "invalid_transition" | "unknown_transition";
      readonly from: BookingStatus;
      readonly transition: string;
    };

/**
 * Apply a transition, or explain why it cannot happen.
 *
 * Returns a result rather than throwing: a renter cancelling a booking that an
 * admin just cancelled is an expected outcome, not an exceptional one.
 */
export function applyTransition(
  current: BookingStatus,
  transition: BookingTransition,
  context: TransitionContext,
): TransitionResult {
  const rule = TRANSITIONS[transition];
  // The type says this cannot be undefined, but the value arrives from a JSON
  // request body via a cast. Without this the whole request 500s on a typo.
  if (!rule) {
    return { ok: false, reason: "unknown_transition", from: current, transition };
  }
  if (!rule.from.includes(current)) {
    return { ok: false, reason: "invalid_transition", from: current, transition };
  }

  const event: AuditEvent = {
    bookingId: context.bookingId,
    transition,
    from: current,
    to: rule.to,
    label: rule.label,
    actorId: context.actorId,
    at: context.at.toISOString(),
    ...(context.note === undefined ? {} : { note: context.note }),
  };

  return { ok: true, status: rule.to, event };
}

/**
 * Where a transition lands, independent of where it starts. Lets an endpoint
 * recognise a retry: "the booking is already in this transition's target
 * status" is a success to repeat, not a conflict to report.
 */
export function transitionTarget(transition: BookingTransition): BookingStatus {
  return TRANSITIONS[transition].to;
}

/**
 * The Dutch audit label of a transition, without a booking in hand. The
 * scheduler's set-based expiry sweep writes audit rows for bookings it has not
 * loaded, and must not invent its own copy of the table's wording.
 */
export function transitionLabel(transition: BookingTransition): string {
  return TRANSITIONS[transition].label;
}

/** Every transition legal from this status. Drives which buttons the admin UI shows. */
export function allowedTransitions(status: BookingStatus): BookingTransition[] {
  return BOOKING_TRANSITIONS.filter((transition) => TRANSITIONS[transition].from.includes(status));
}

export function isTerminal(status: BookingStatus): boolean {
  return TERMINAL.includes(status);
}

/** False once a contract exists. See PRICES_FROZEN_IN. */
export function canEditPrices(status: BookingStatus): boolean {
  return !PRICES_FROZEN_IN.includes(status);
}

/** True once the stay has happened and the settlement flow is unlocked. */
export function canSettle(status: BookingStatus): boolean {
  return status === "stayed";
}

/**
 * True while the booking occupies its dates. Mirrors the SQL exclusion
 * constraint's partial WHERE — an integration test reads the predicate out of
 * `pg_constraint` and asserts the two agree, because a comment cannot.
 *
 * `settled` and `closed` still occupy: a completed stay must not have its dates
 * become bookable again, or the occupancy history that the logiesdecreet and
 * subsidy counters read from is corrupt.
 *
 * `enquiry` deliberately does not. Several groups asking about the same weekend
 * is normal and the admin picks one — the dates are only held from `option` on.
 * Expect a volunteer to report this as a bug in week one; it isn't.
 */
const OCCUPYING: readonly BookingStatus[] = ["option", "confirmed", "stayed", "settled", "closed"];

export function occupiesDates(status: BookingStatus): boolean {
  return OCCUPYING.includes(status);
}
